export type ZeruzBubblePosition = "left" | "right";
export type ZeruzBubbleUi = "embed" | "bubble" | "full";
type WidgetState = "closed" | "hint" | "open";

export interface ZeruzBubbleSize {
  width: string | number;
  height: string | number;
}

export interface ZeruzBubbleChatOptions {
  slug?: string;
  baseUrl?: string;
  iframeSrc?: string;
  ui?: ZeruzBubbleUi;
  hiddenBranding?: boolean;
  position?: ZeruzBubblePosition;
  bottom?: string | number;
  sideOffset?: string | number;
  zIndex?: number;
  containerId?: string;
  iframeId?: string;
  title?: string;
  allow?: string;
  referrerPolicy?: ReferrerPolicy;
  params?: Record<string, string | number | boolean | null | undefined>;
  closedSize?: ZeruzBubbleSize;
  hintSize?: ZeruzBubbleSize;
  openSize?: ZeruzBubbleSize;
  mobileBreakpoint?: number;
}

interface NormalizedOptions extends Required<Omit<ZeruzBubbleChatOptions, "slug" | "baseUrl" | "iframeSrc" | "params">> {
  slug?: string;
  baseUrl?: string;
  iframeSrc?: string;
  params?: ZeruzBubbleChatOptions["params"];
}

interface LightboxState {
  overlay: HTMLDivElement;
  onKeyDown: (event: KeyboardEvent) => void;
}

interface BubbleMessagePayload {
  open?: boolean;
  hintVisible?: boolean;
  src?: string;
  alt?: string;
}

const DEFAULT_OPTIONS: NormalizedOptions = {
  ui: "embed",
  hiddenBranding: true,
  position: "right",
  bottom: "16px",
  sideOffset: "16px",
  zIndex: 999999999,
  containerId: "zeruz-bubble",
  iframeId: "zeruz-bubble-iframe",
  title: "Bubble Chat - Agente IA",
  allow: "clipboard-read; clipboard-write",
  referrerPolicy: "no-referrer",
  closedSize: { width: "88px", height: "88px" },
  hintSize: { width: "min(92vw, 380px)", height: "140px" },
  openSize: { width: "min(92vw, 440px)", height: "min(82dvh, 760px)" },
  mobileBreakpoint: 640,
};

const CHAT_OPEN_EVENT = "zeruz:chat:open";
const CHAT_CLOSE_EVENT = "zeruz:chat:close";
const CHAT_HINT_SHOW_EVENT = "zeruz:chat:hint:show";
const CHAT_HINT_HIDE_EVENT = "zeruz:chat:hint:hide";
const CHAT_STATE_EVENT = "zeruz:chat:state";
const IMAGE_LIGHTBOX_OPEN_EVENT = "zeruz:image:open";

function canUseDom(): boolean {
  return typeof window !== "undefined" && typeof document !== "undefined";
}

function toCssSize(value: string | number): string {
  return typeof value === "number" ? `${value}px` : String(value);
}

function normalizePosition(position: ZeruzBubbleChatOptions["position"]): ZeruzBubblePosition {
  return position === "left" ? "left" : "right";
}

function normalizeBaseUrl(baseUrl?: string): string {
  if (baseUrl) return String(baseUrl).replace(/\/+$/, "");
  if (canUseDom()) return window.location.origin;
  return "";
}

function mergeOptions(options: ZeruzBubbleChatOptions = {}): NormalizedOptions {
  return {
    ...DEFAULT_OPTIONS,
    ...options,
    closedSize: {
      ...DEFAULT_OPTIONS.closedSize,
      ...(options.closedSize || {}),
    },
    hintSize: {
      ...DEFAULT_OPTIONS.hintSize,
      ...(options.hintSize || {}),
    },
    openSize: {
      ...DEFAULT_OPTIONS.openSize,
      ...(options.openSize || {}),
    },
  };
}

function appendParams(url: URL, params?: ZeruzBubbleChatOptions["params"]): URL {
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    url.searchParams.set(key, String(value));
  });
  return url;
}

export function buildIframeSrc(options: ZeruzBubbleChatOptions = {}): string {
  const merged = mergeOptions(options);
  if (merged.iframeSrc) {
    const iframeUrl = new URL(merged.iframeSrc, canUseDom() ? window.location.href : "http://localhost");
    appendParams(iframeUrl, {
      ...merged.params,
      position: normalizePosition(merged.position),
    });
    return iframeUrl.toString();
  }

  if (!merged.slug) {
    throw new Error("ZeruzBubbleChat: informe `slug` ou `iframeSrc`.");
  }

  const baseUrl = normalizeBaseUrl(merged.baseUrl);
  if (!baseUrl) {
    throw new Error("ZeruzBubbleChat: `baseUrl` é obrigatório fora do navegador.");
  }

  const safeSlug = encodeURIComponent(merged.slug);
  const safeUi = encodeURIComponent(merged.ui);
  const hiddenSuffix = merged.hiddenBranding ? "/hidden" : "";
  const iframeUrl = new URL(`/chat/${safeSlug}/${safeUi}${hiddenSuffix}`, `${baseUrl}/`);
  appendParams(iframeUrl, {
    ...merged.params,
    position: normalizePosition(merged.position),
  });
  return iframeUrl.toString();
}

function getMessageType(data: unknown): string {
  if (typeof data === "string") return data;
  if (data && typeof data === "object" && "type" in data) {
    return String((data as { type?: unknown }).type || "");
  }
  return "";
}

function getMessagePayload(data: unknown): BubbleMessagePayload {
  if (data && typeof data === "object" && "payload" in data) {
    const payload = (data as { payload?: unknown }).payload;
    if (payload && typeof payload === "object") {
      return payload as BubbleMessagePayload;
    }
  }
  return {};
}

export class ZeruzBubbleChatWidget {
  private options: NormalizedOptions;
  private state: WidgetState = "closed";
  private container: HTMLDivElement | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private expectedOrigin = "";
  private lightbox: LightboxState | null = null;
  private previousBodyOverflow: string | null = null;

  constructor(options: ZeruzBubbleChatOptions = {}) {
    this.options = mergeOptions(options);
    this.handleMessage = this.handleMessage.bind(this);
    this.handleResize = this.handleResize.bind(this);
  }

  mount(): ZeruzBubbleChatWidget {
    if (!canUseDom()) {
      throw new Error("ZeruzBubbleChat: DOM indisponível.");
    }

    if (this.container) return this;

    const iframeSrc = buildIframeSrc(this.options);
    this.expectedOrigin = new URL(iframeSrc).origin;

    this.container = document.createElement("div");
    this.container.id = this.options.containerId;
    this.container.setAttribute("data-zeruz-bubble-chat", "true");

    this.iframe = document.createElement("iframe");
    this.iframe.id = this.options.iframeId;
    this.iframe.title = this.options.title;
    this.iframe.src = iframeSrc;
    this.iframe.allow = this.options.allow;
    this.iframe.loading = "lazy";
    this.iframe.referrerPolicy = this.options.referrerPolicy;
    this.iframe.setAttribute("allowtransparency", "true");
    this.iframe.setAttribute("scrolling", "no");
    this.iframe.style.border = "0";
    this.iframe.style.background = "transparent !important";
    this.iframe.style.backgroundColor = "transparent !important";
    this.iframe.style.display = "block";
    this.iframe.style.width = "100%";
    this.iframe.style.height = "100%";
    this.iframe.style.overflow = "hidden";

    this.container.appendChild(this.iframe);
    document.body.appendChild(this.container);

    window.addEventListener("message", this.handleMessage);
    window.addEventListener("resize", this.handleResize);
    this.applyContainerFrame();
    return this;
  }

  update(options: Partial<ZeruzBubbleChatOptions> = {}): ZeruzBubbleChatWidget {
    this.options = mergeOptions({ ...this.options, ...options });
    if (
      this.iframe
      && (
        options.slug
        || options.baseUrl
        || options.iframeSrc
        || options.ui
        || options.hiddenBranding !== undefined
        || options.params
        || options.position
      )
    ) {
      const iframeSrc = buildIframeSrc(this.options);
      this.expectedOrigin = new URL(iframeSrc).origin;
      this.iframe.src = iframeSrc;
    }
    this.applyContainerFrame();
    return this;
  }

  destroy(): void {
    if (!canUseDom()) return;
    window.removeEventListener("message", this.handleMessage);
    window.removeEventListener("resize", this.handleResize);
    this.closeLightbox();
    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.container = null;
    this.iframe = null;
  }

  getIframe(): HTMLIFrameElement | null {
    return this.iframe;
  }

  getContainer(): HTMLDivElement | null {
    return this.container;
  }

  private handleMessage(event: MessageEvent): void {
    if (this.expectedOrigin && event.origin !== this.expectedOrigin) return;

    const type = getMessageType(event.data);
    const payload = getMessagePayload(event.data);

    if (type === CHAT_OPEN_EVENT) {
      this.state = "open";
      this.applyContainerFrame();
      return;
    }

    if (type === CHAT_CLOSE_EVENT) {
      this.state = "closed";
      this.applyContainerFrame();
      return;
    }

    if (type === CHAT_HINT_SHOW_EVENT) {
      this.state = "hint";
      this.applyContainerFrame();
      return;
    }

    if (type === CHAT_HINT_HIDE_EVENT) {
      this.state = "closed";
      this.applyContainerFrame();
      return;
    }

    if (type === CHAT_STATE_EVENT) {
      this.state = payload.open ? "open" : payload.hintVisible ? "hint" : "closed";
      this.applyContainerFrame();
      return;
    }

    if (type === IMAGE_LIGHTBOX_OPEN_EVENT && payload.src) {
      this.openLightbox(payload);
    }
  }

  private handleResize(): void {
    this.applyContainerFrame();
  }

  private isMobileOpen(): boolean {
    if (!canUseDom() || this.state !== "open") return false;
    return window.innerWidth < Number(this.options.mobileBreakpoint || DEFAULT_OPTIONS.mobileBreakpoint);
  }

  private getActiveSize(): ZeruzBubbleSize {
    if (this.state === "open") return this.options.openSize;
    if (this.state === "hint") return this.options.hintSize;
    return this.options.closedSize;
  }

  private applyContainerFrame(): void {
    if (!this.container) return;

    const position = normalizePosition(this.options.position);
    const mobileOpen = this.isMobileOpen();
    const size = this.getActiveSize();
    const { style } = this.container;

    style.position = "fixed";
    style.zIndex = String(this.options.zIndex);
    style.pointerEvents = "auto";
    style.overflow = "hidden";
    style.background = "transparent";
    style.backgroundColor = "transparent";
    style.border = "0";

    if (mobileOpen) {
      style.inset = "0";
      style.width = "100vw";
      style.height = "100dvh";
      style.left = "0";
      style.right = "0";
      style.bottom = "0";
      return;
    }

    style.inset = "auto";
    style.bottom = toCssSize(this.options.bottom);
    style.width = toCssSize(size.width);
    style.height = toCssSize(size.height);

    if (position === "left") {
      style.left = toCssSize(this.options.sideOffset);
      style.right = "auto";
    } else {
      style.right = toCssSize(this.options.sideOffset);
      style.left = "auto";
    }
  }

  private openLightbox(payload: BubbleMessagePayload): void {
    if (!payload.src) return;
    this.closeLightbox();

    const overlay = document.createElement("div");
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.style.position = "fixed";
    overlay.style.inset = "0";
    overlay.style.background = "rgba(0, 0, 0, 0.9)";
    overlay.style.display = "flex";
    overlay.style.alignItems = "center";
    overlay.style.justifyContent = "center";
    overlay.style.zIndex = "2147483647";
    overlay.style.padding = "24px";
    overlay.style.cursor = "zoom-out";

    const image = document.createElement("img");
    image.src = payload.src;
    image.alt = payload.alt || "Imagem ampliada";
    image.style.maxWidth = "100%";
    image.style.maxHeight = "100%";
    image.style.objectFit = "contain";
    image.style.display = "block";
    image.style.cursor = "auto";

    image.addEventListener("click", (event) => event.stopPropagation());
    overlay.addEventListener("click", () => this.closeLightbox());

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") this.closeLightbox();
    };
    document.addEventListener("keydown", onKeyDown);

    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    overlay.appendChild(image);
    document.body.appendChild(overlay);
    this.lightbox = { overlay, onKeyDown };
  }

  private closeLightbox(): void {
    if (!this.lightbox) return;
    document.removeEventListener("keydown", this.lightbox.onKeyDown);
    if (this.lightbox.overlay.parentNode) {
      this.lightbox.overlay.parentNode.removeChild(this.lightbox.overlay);
    }
    document.body.style.overflow = this.previousBodyOverflow || "";
    this.previousBodyOverflow = null;
    this.lightbox = null;
  }
}

let currentInstance: ZeruzBubbleChatWidget | null = null;

export function init(options: ZeruzBubbleChatOptions): ZeruzBubbleChatWidget {
  if (currentInstance) currentInstance.destroy();
  currentInstance = new ZeruzBubbleChatWidget(options).mount();
  return currentInstance;
}

export function destroy(): void {
  if (!currentInstance) return;
  currentInstance.destroy();
  currentInstance = null;
}

export function getInstance(): ZeruzBubbleChatWidget | null {
  return currentInstance;
}

export const ZeruzBubbleChat = {
  init,
  destroy,
  getInstance,
  buildIframeSrc,
  Widget: ZeruzBubbleChatWidget,
};

export default ZeruzBubbleChat;
